import styled, { keyframes, css } from 'styled-components';
import { theme } from '../theme';

// Animations
const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 10px rgba(255, 107, 129, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 107, 129, 0.8);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fireAnimation = keyframes`
  0%, 100% { transform: scale(1) rotate(0deg); }
  25% { transform: scale(1.1) rotate(-5deg); }
  75% { transform: scale(1.1) rotate(5deg); }
`;

// Game Controller Nav Container
export const GameControllerNavContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${theme.colors.background.overlay};
  backdrop-filter: blur(10px);
  border-bottom: 2px solid ${theme.colors.border.light};
  box-shadow: ${theme.shadows.medium};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.md};
  min-height: 70px;
  width: 100%;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    flex-wrap: wrap;
    gap: ${theme.spacing.sm};
    min-height: auto;
    padding-bottom: ${theme.spacing.md};
  }
  
  @media (max-width: 400px) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
  }
`;

// Left Section - Game Hub
export const GameHubSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    order: 1;
  }
`;

export const GameHubButton = styled.button`
  background: ${theme.colors.background.secondary};
  border: 2px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.full};
  width: 50px;
  height: 50px;
  min-width: 50px;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  flex-shrink: 0;
  
  &:hover {
    transform: scale(1.1);
    background: ${theme.colors.background.tertiary};
    border-color: ${theme.colors.border.orange};
  }
  
  &:active {
    transform: scale(0.95);
    background: ${theme.colors.background.tertiary};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 48px;
    height: 48px;
    min-width: 48px;
    min-height: 48px;
    
    &:hover {
      transform: none;
    }
  }
  
  @media (max-width: 400px) {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
  }
`;

export const GameHubIcon = styled.span`
  font-size: 1.8rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.6rem;
  }
  
  @media (max-width: 400px) {
    font-size: 1.4rem;
  }
`;

export const GameHubLabel = styled.span`
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 0.8rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`;

// Center Section - Game Type Pills
export const GameTypePillsSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  justify-content: center;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  scroll-behavior: smooth;
  padding: ${theme.spacing.xs} 0;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    order: 3;
    width: 100%;
    justify-content: flex-start;
    padding: ${theme.spacing.sm} 0 0 0;
    gap: ${theme.spacing.xs};
    margin: 0 -${theme.spacing.xs};
    padding-left: ${theme.spacing.xs};
    padding-right: ${theme.spacing.xs};
  }
`;

export const PillWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
  
  /* Disable hover preview on mobile */
  @media (max-width: ${theme.breakpoints.mobile}) {
    & > div:last-child {
      display: none !important;
    }
  }
`;

export const GameTypePill = styled.button<{ $color: string; $isActive?: boolean }>`
  background: ${props => props.$isActive 
    ? theme.colors.gradients.primary
    : theme.colors.background.secondary
  };
  border: 2px solid ${props => props.$isActive 
    ? theme.colors.border.orange 
    : theme.colors.border.light
  };
  border-radius: ${theme.borderRadius.full};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  color: ${props => props.$isActive ? 'white' : theme.colors.text.primary};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  box-shadow: ${props => props.$isActive 
    ? theme.shadows.medium
    : theme.shadows.light
  };
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    background: ${props => props.$isActive 
      ? theme.colors.gradients.primary
      : theme.colors.background.tertiary
    };
    border-color: ${theme.colors.border.orange};
    box-shadow: ${theme.shadows.medium};
  }
  
  &:active {
    transform: translateY(0) scale(0.95);
    background: ${props => props.$isActive 
      ? theme.colors.gradients.primary
      : theme.colors.background.secondary
    };
  }
  
  min-height: 44px; /* iOS touch target minimum */
  user-select: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: 0.85rem;
    min-height: 48px; /* Android touch target */
    
    &:hover {
      transform: none; /* Disable hover on mobile */
      background: ${props => props.$isActive 
        ? theme.colors.gradients.primary
        : theme.colors.background.secondary
      };
    }
  }
  
  @media (max-width: 400px) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: 0.8rem;
    min-height: 44px;
  }
  
  @media (max-width: 350px) {
    padding: 6px 10px;
    font-size: 0.75rem;
  }
`;

export const PillIcon = styled.span`
  font-size: 1.2rem;
  line-height: 1;
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.15rem;
  }
  
  @media (max-width: 350px) {
    font-size: 1.1rem;
  }
`;

export const PillLabel = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 400px) {
    font-size: 0.75rem;
  }
  
  @media (max-width: 360px) {
    display: none;
  }
`;

export const PillBadge = styled.span`
  background: rgba(255, 255, 255, 0.3);
  border-radius: ${theme.borderRadius.full};
  padding: 2px 6px;
  font-size: 0.7rem;
  font-weight: 700;
  margin-left: 4px;
`;

// Right Section - User Progress Widget
export const UserProgressSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    order: 2;
    gap: ${theme.spacing.sm};
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  
  @media (max-width: 400px) {
    gap: ${theme.spacing.xs};
  }
`;

export const ProgressWidget = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    gap: 2px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none; /* Hide XP progress on mobile to save space */
  }
`;

export const LevelBadge = styled.div`
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: ${theme.borderRadius.md};
  padding: 4px 12px;
  color: white;
  font-size: 0.85rem;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  box-shadow: 0 2px 8px rgba(255, 165, 0, 0.4);
  white-space: nowrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none; /* Hide on mobile to save space */
  }
`;

export const XPProgressBar = styled.div`
  width: 120px;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  position: relative;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 100px;
  }
`;

export const XPProgressFill = styled.div<{ $progress: number }>`
  width: ${props => props.$progress}%;
  height: 100%;
  background: linear-gradient(90deg, #4CAF50 0%, #8BC34A 100%);
  border-radius: ${theme.borderRadius.full};
  transition: width 0.5s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

export const XPText = styled.span`
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
`;

export const StatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.xs};
  }
`;

export const StatBadge = styled.button<{ $color?: string }>`
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: ${theme.borderRadius.md};
  padding: 6px 10px;
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  min-width: 44px;
  min-height: 44px;
  justify-content: center;
  
  ${props => props.$color && `
    border-color: ${props.$color}80;
    box-shadow: 0 0 10px ${props.$color}40;
  `}
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
    background: rgba(255, 255, 255, 0.3);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 8px;
    font-size: 0.8rem;
    min-width: 48px;
    min-height: 48px;
    
    &:hover {
      transform: none;
    }
  }
  
  @media (max-width: 400px) {
    padding: 6px;
    min-width: 44px;
    min-height: 44px;
  }
`;

export const StatIcon = styled.span<{ $animate?: boolean }>`
  font-size: 1rem;
  line-height: 1;
  
  ${props => props.$animate && css`
    animation: ${fireAnimation} 1s ease-in-out infinite;
  `}
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

export const StatValue = styled.span`
  font-weight: 700;
  font-size: 0.9rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.85rem;
  }
  
  @media (max-width: 400px) {
    display: none;
  }
`;

export const NotificationDot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #FF4444;
  border: 2px solid white;
  border-radius: 50%;
  ${css`animation: ${pulseGlow} 2s ease-in-out infinite;`}
`;

export const PremiumBadge = styled.button`
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: ${theme.borderRadius.md};
  padding: 6px 12px;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 0 15px rgba(255, 165, 0, 0.5);
  min-width: 44px;
  min-height: 44px;
  
  &:hover {
    transform: scale(1.1) rotate(5deg);
    box-shadow: 0 0 25px rgba(255, 165, 0, 0.8);
  }
  
  &:active {
    transform: scale(0.95) rotate(0deg);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 8px;
    font-size: 1.15rem;
    min-width: 48px;
    min-height: 48px;
    
    &:hover {
      transform: scale(1.05) rotate(0deg);
    }
  }
  
  @media (max-width: 400px) {
    padding: 6px;
    font-size: 1.1rem;
    min-width: 44px;
    min-height: 44px;
  }
`;

// Hover Preview Card (for pill hover)
export const PillPreview = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%) ${props => props.$isVisible ? 'translateY(0)' : 'translateY(-10px)'};
  opacity: ${props => props.$isVisible ? 1 : 0};
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  min-width: 250px;
  max-width: 300px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  ${props => props.$isVisible && css`animation: ${slideIn} 0.3s ease;`}
  
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid white;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: none;
  }
`;

export const PreviewTitle = styled.h4`
  font-size: 1rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 ${theme.spacing.sm} 0;
`;

export const PreviewDescription = styled.p`
  font-size: 0.85rem;
  color: #666;
  margin: 0 0 ${theme.spacing.sm} 0;
  line-height: 1.4;
`;

export const PreviewGames = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

export const PreviewGameItem = styled.button`
  background: rgba(102, 126, 234, 0.08);
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #333;
  font-size: 0.85rem;
  
  &:hover {
    background: rgba(102, 126, 234, 0.15);
    transform: translateX(4px);
  }
`;

